import type { Container, Commodity } from '@/types/container';

/**
 * Estrae il paese da una stringa di route (formato: "Città, Paese" o "Paese, Città")
 */
export function extractCountryFromRoute(routeString: string): string {
    const parts = routeString.split(',').map(part => part.trim());
    
    if (parts.length < 2) {
        return routeString; // Fallback se non riesce a parsare
    }
    
    // Lista completa di paesi (in varie lingue e formati)
    const countries = new Set([
        // Paesi in italiano/inglese
        'USA', 'United States', 'Stati Uniti',
        'Peru', 'Perù', 'Perú',
        'Italia', 'Italy',
        'Kenya',
        'Chile', 'Cile',
        'Spagna', 'Spain',
        'Ecuador',
        'Francia', 'France',
        'Filippine', 'Philippines',
        'Paesi Bassi', 'Netherlands', 'Olanda',
        'Ghana',
        'Costa Rica',
        'Brasile', 'Brazil',
        'Bangladesh',
        'Germania', 'Germany',
        'Cina', 'China',
        'Vietnam',
        'Colombia', 'Colombia',
        'Thailandia', 'Thailand',
        'India',
        'Messico', 'Mexico',
        'UK', 'United Kingdom', 'Regno Unito',
        'Canada',
        'Australia',
        'Argentina',
        'Indonesia',
        'Turchia', 'Turkey',
        'Sudafrica', 'South Africa',
        'Egitto', 'Egypt',
        'Marocco', 'Morocco',
        'Tunisia',
        'Algeria',
        'Nigeria',
        'Senegal',
        'Madagascar',
        'Mauritius',
        'Sri Lanka',
        'Myanmar',
        'Cambogia', 'Cambodia',
        'Laos',
        'Malaysia',
        'Singapore',
        'Corea del Sud', 'South Korea',
        'Giappone', 'Japan',
        'Taiwan',
        'Nuova Zelanda', 'New Zealand',
        'Cile', 'Chile',
        'Uruguay',
        'Paraguay',
        'Bolivia',
        'Venezuela',
        'Panama',
        'Guatemala',
        'Honduras',
        'Nicaragua',
        'El Salvador',
        'Repubblica Dominicana', 'Dominican Republic',
        'Giamaica', 'Jamaica',
        'Cuba',
        'Haiti',
        'Trinidad e Tobago', 'Trinidad and Tobago',
        'Belize',
        'Suriname',
        'Guyana',
        'Polonia', 'Poland',
        'Repubblica Ceca', 'Czech Republic',
        'Ungheria', 'Hungary',
        'Romania',
        'Bulgaria',
        'Grecia', 'Greece',
        'Portogallo', 'Portugal',
        'Croazia', 'Croatia',
        'Slovenia',
        'Slovacchia', 'Slovakia',
        'Austria',
        'Svizzera', 'Switzerland',
        'Belgio', 'Belgium',
        'Danimarca', 'Denmark',
        'Svezia', 'Sweden',
        'Norvegia', 'Norway',
        'Finlandia', 'Finland',
        'Islanda', 'Iceland',
        'Irlanda', 'Ireland',
        'Lussemburgo', 'Luxembourg',
        'Malta',
        'Cipro', 'Cyprus',
        'Estonia',
        'Lettonia', 'Latvia',
        'Lituania', 'Lithuania',
    ]);
    
    const firstPart = parts[0];
    const secondPart = parts[1];
    
    // Controlla se il primo elemento è un paese (formato "Paese, Città")
    if (countries.has(firstPart)) {
        return firstPart;
    }
    
    // Controlla se il secondo elemento è un paese (formato "Città, Paese")
    if (countries.has(secondPart)) {
        return secondPart;
    }
    
    // Se nessuno dei due è riconosciuto come paese, prova a normalizzare
    // Rimuovi prefissi comuni come "Porto di"
    const normalizedSecond = secondPart.replace(/^Porto di\s*/i, '').trim();
    if (countries.has(normalizedSecond)) {
        return normalizedSecond;
    }
    
    // Fallback: usa il secondo elemento (assumendo formato standard "Città, Paese")
    return secondPart;
}

/**
 * Mappa una destinazione a un porto specifico
 */
export function mapToSpecificPort(destination: string): string {
    const portMapping: Record<string, string> = {
        // Porti italiani
        'Porto di Genova, Italia': 'Porto di Genova',
        'Porto di Livorno, Italia': 'Porto di Livorno', 
        'Civitavecchia, Italia': 'Porto di Civitavecchia',
        
        // Altri porti europei
        'Rotterdam, Netherlands': 'Porto di Rotterdam',
        'Rotterdam, Paesi Bassi': 'Porto di Rotterdam',
        'Barcellona, Spagna': 'Porto di Barcellona',
        'FOS, Francia': 'Porto di FOS',
        
        // Fallback per altre destinazioni
        'London, UK': 'Porto di Londra',
        'Amsterdam, Netherlands': 'Porto di Amsterdam',
        'Le Havre, France': 'Porto di Le Havre'
    };
    
    return portMapping[destination] || extractPortFromDestination(destination);
}

/**
 * Estrae il nome del porto da una destinazione generica
 */
function extractPortFromDestination(destination: string): string {
    // Se contiene già "Porto di", lo mantiene
    if (destination.includes('Porto di')) {
        return destination;
    }
    
    // Estrae la città e aggiunge "Porto di"
    const city = destination.split(',')[0].trim();
    return `Porto di ${city}`;
}

/**
 * Traduce il nome della commodity per la visualizzazione
 */
export function translateCommodity(commodity: Commodity, locale: string = 'it'): string {
    const translations: Record<string, Record<Commodity, string>> = {
        it: {
            avocado: 'Avocado',
            blueberries: 'Mirtilli',
            banana: 'Banane',
            mango: 'Mango',
            pineapple: 'Ananas',
            coffee: 'Caffè',
            cocoa: 'Cacao',
            sugar: 'Zucchero',
            electronics: 'Elettronica',
            textiles: 'Tessili',
            machinery: 'Macchinari'
        },
        en: {
            avocado: 'Avocado',
            blueberries: 'Blueberries',
            banana: 'Bananas',
            mango: 'Mango',
            pineapple: 'Pineapple',
            coffee: 'Coffee',
            cocoa: 'Cocoa',
            sugar: 'Sugar',
            electronics: 'Electronics',
            textiles: 'Textiles',
            machinery: 'Machinery'
        }
    };
    
    return translations[locale]?.[commodity] || commodity;
}

/**
 * Aggrega i dati dei container per commodity
 */
export function aggregateCommodityData(containers: Container[]): Record<Commodity, number> {
    return containers.reduce((acc, container) => {
        acc[container.commodity] = (acc[container.commodity] || 0) + 1;
        return acc;
    }, {} as Record<Commodity, number>);
}

/**
 * Aggrega i dati dei container per paese di origine
 */
export function aggregateOriginData(containers: Container[]): Record<string, number> {
    return containers.reduce((acc, container) => {
        const country = extractCountryFromRoute(container.route.from);
        acc[country] = (acc[country] || 0) + 1;
        return acc;
    }, {} as Record<string, number>);
}

/**
 * Aggrega i dati dei container per porto di arrivo
 */
export function aggregatePortData(containers: Container[]): Record<string, number> {
    return containers.reduce((acc, container) => {
        const port = mapToSpecificPort(container.route.to);
        acc[port] = (acc[port] || 0) + 1;
        return acc;
    }, {} as Record<string, number>);
}

/**
 * Mappa un nome commodity tradotto al valore originale
 */
export function mapCommodityDisplayToValue(displayName: string, locale: string = 'it'): Commodity | null {
    const translations: Record<string, Record<string, Commodity>> = {
        it: {
            'Avocado': 'avocado',
            'Mirtilli': 'blueberries',
            'Banane': 'banana',
            'Mango': 'mango',
            'Ananas': 'pineapple',
            'Caffè': 'coffee',
            'Cacao': 'cocoa',
            'Zucchero': 'sugar',
            'Elettronica': 'electronics',
            'Tessili': 'textiles',
            'Macchinari': 'machinery'
        },
        en: {
            'Avocado': 'avocado',
            'Blueberries': 'blueberries',
            'Bananas': 'banana',
            'Mango': 'mango',
            'Pineapple': 'pineapple',
            'Coffee': 'coffee',
            'Cocoa': 'cocoa',
            'Sugar': 'sugar',
            'Electronics': 'electronics',
            'Textiles': 'textiles',
            'Machinery': 'machinery'
        }
    };
    
    return translations[locale]?.[displayName] || null;
}

/**
 * Mappa un nome porto display al valore filtro
 */
export function mapPortDisplayToValue(displayName: string): string {
    // I nomi dei porti sono già in formato corretto per il filtro
    return displayName;
}

/**
 * Mappa un nome paese display al valore filtro
 */
export function mapOriginDisplayToValue(displayName: string): string {
    // I nomi dei paesi sono già in formato corretto per il filtro
    return displayName;
}
